import { createLeadsService } from '../services/leadsFactory';
import type { FormData } from '../types/form';
import { ApiError, type CreateContactResponse, type AvailabilityResponse } from '../types/api';
import type { ILeadsService } from '../services/leads';
import { storageController } from './StorageController';

export type ContactResult = {
  success: boolean;
  status?: 'accepted' | 'accepted_tier2' | 'rejected' | 'ERROR';
  error?: string;
  code?: number;
};

export type AvailabilityResult = {
  success: boolean;
  capacity?: number;
  availability: 'none' | 'low' | 'high';
  error?: string;
};

export type ScheduleResult = {
  success: boolean;
  error?: string;
};

export type CallResult = {
  success: boolean;
  from?: string;
  error?: string;
};

export type TimeslotData = {
  timeSlot: string;
  isAvailable: boolean;
  capacity: number;
  booked: number;
  slotsAvailable: number;
};

export type DateAvailabilityResult = {
  success: boolean;
  date: string;
  totalCalls?: number;
  timeslots: TimeslotData[];
  error?: string;
  cachedAt?: number;
};

type AvailabilityCache = {
  [date: string]: {
    data: DateAvailabilityResult;
    cachedAt: number;
  };
};

const STORAGE_KEYS = {
  SPINWHEEL_ID: 'spinwheel_id',
  HUBSPOT_RECORD_ID: 'hubspot_record_id',
  VERIFICATION_ATTEMPTS: 'verification_attempts',
  CONTACT_FIRST_NAME: 'contact_first_name',
  DEBT_AMOUNT: 'debt_amount',
  AVAILABILITY_CACHE: 'availability_cache',
} as const;

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour in milliseconds

export class FormController {
  private readonly leadsService: ILeadsService;
  private spinwheelId: string = '';
  private hubspotRecordId: string = '';
  private verificationAttempts: number = 0;
  private contactFirstName: string = '';
  private debtAmount: number = 0;
  private availabilityCache: AvailabilityCache = {};

  constructor() {
    this.leadsService = createLeadsService();
    this.loadFromStorage();
  }

  private loadFromStorage() {
    // Load cached values from storage
    this.spinwheelId = localStorage.getItem(STORAGE_KEYS.SPINWHEEL_ID) || '';
    this.hubspotRecordId = localStorage.getItem(STORAGE_KEYS.HUBSPOT_RECORD_ID) || '';
    this.verificationAttempts = parseInt(localStorage.getItem(STORAGE_KEYS.VERIFICATION_ATTEMPTS) || '0', 10);
    this.contactFirstName = localStorage.getItem(STORAGE_KEYS.CONTACT_FIRST_NAME) || '';
    this.debtAmount = parseInt(localStorage.getItem(STORAGE_KEYS.DEBT_AMOUNT) || '0', 10);
    
    // Load availability cache
    const cachedAvailability = sessionStorage.getItem(STORAGE_KEYS.AVAILABILITY_CACHE);
    if (cachedAvailability) {
      try {
        this.availabilityCache = JSON.parse(cachedAvailability);
        // Clean up expired cache entries
        this.cleanupExpiredCache();
      } catch (error) {
        console.warn('Failed to parse availability cache:', error);
        this.availabilityCache = {};
      }
    }
  }

  private saveToStorage() {
    // Save current values to storage
    localStorage.setItem(STORAGE_KEYS.SPINWHEEL_ID, this.spinwheelId);
    localStorage.setItem(STORAGE_KEYS.HUBSPOT_RECORD_ID, this.hubspotRecordId);
    localStorage.setItem(STORAGE_KEYS.VERIFICATION_ATTEMPTS, this.verificationAttempts.toString());
    localStorage.setItem(STORAGE_KEYS.CONTACT_FIRST_NAME, this.contactFirstName);
    localStorage.setItem(STORAGE_KEYS.DEBT_AMOUNT, this.debtAmount.toString());
  }

  private clearStorage() {
    // Clear all stored values
    localStorage.removeItem(STORAGE_KEYS.SPINWHEEL_ID);
    localStorage.removeItem(STORAGE_KEYS.HUBSPOT_RECORD_ID);
    localStorage.removeItem(STORAGE_KEYS.VERIFICATION_ATTEMPTS);
    localStorage.removeItem(STORAGE_KEYS.CONTACT_FIRST_NAME);
    localStorage.removeItem(STORAGE_KEYS.DEBT_AMOUNT);
    sessionStorage.removeItem(STORAGE_KEYS.AVAILABILITY_CACHE);
  }

  private saveAvailabilityCache() {
    try {
      sessionStorage.setItem(STORAGE_KEYS.AVAILABILITY_CACHE, JSON.stringify(this.availabilityCache));
    } catch (error) {
      console.warn('Failed to save availability cache:', error);
    }
  }

  private cleanupExpiredCache() {
    const now = Date.now();
    const expiredKeys: string[] = [];
    
    for (const [date, cacheEntry] of Object.entries(this.availabilityCache)) {
      if (now - cacheEntry.cachedAt > CACHE_TTL_MS) {
        expiredKeys.push(date);
      }
    }
    
    expiredKeys.forEach(key => {
      delete this.availabilityCache[key];
    });
    
    if (expiredKeys.length > 0) {
      this.saveAvailabilityCache();
    }
  }

  private isCacheValid(date: string): boolean {
    const cacheEntry = this.availabilityCache[date];
    if (!cacheEntry) {
      return false;
    }
    
    const now = Date.now();
    return (now - cacheEntry.cachedAt) < CACHE_TTL_MS;
  }

  public getContactFirstName(): string {
    return this.contactFirstName;
  }

  public getDebtAmount(): number {
    return this.debtAmount;
  }

  private handleApiError(err: unknown): { success: false; error: string; code?: number } {
    if (err instanceof ApiError) {
      return { 
        success: false, 
        error: err.status.message,
        code: err.status.code
      };
    }

    if (err && typeof err === 'object' && 'status' in err) {
      const status = (err as any).status;
      if (status && typeof status === 'object' && 'code' in status && 'message' in status) {
        return {
          success: false,
          error: status.message,
          code: status.code
        };
      }
    }
    
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'An error occurred' 
    };
  }

  private isSuccessStatus(code: number): boolean {
    return code >= 200 && code < 300;
  }

  private getAvailabilityLevel(capacity: number): 'none' | 'low' | 'high' {
    if (capacity === 0) return 'none';
    if (capacity <= 2) return 'low';
    return 'high';
  }

  async connectBySms(
    phoneNumber: string, 
    email: string, 
    dateOfBirth: string,
    loanAmount: number,
    loanPurpose: string,
    employmentStatus: string,
    employmentFrequency: string,
    educationLevel: string,
    annualIncome: number,
    propertyStatus: string,
    ipAddress: string,
    marketingParams: Record<string, string>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.leadsService.connectBySms(
        phoneNumber,
        email,
        dateOfBirth,
        loanAmount,
        loanPurpose,
        employmentStatus,
        employmentFrequency,
        educationLevel,
        annualIncome,
        propertyStatus,
        ipAddress,
        marketingParams
      );

      if (this.isSuccessStatus(response.status.code) && response.spinwheelId) {
        this.spinwheelId = response.spinwheelId;
        if (response.hubspotRecordId) {
          this.hubspotRecordId = response.hubspotRecordId;
        }
        this.saveToStorage();
        return { success: true };
      }
      
      return { 
        success: false, 
        error: 'Failed to create connection' 
      };
    } catch (err) {
      return this.handleApiError(err);
    }
  }

  async verifyCode(code: string, formData: FormData): Promise<{ success: boolean; error?: string }> {
    if (!this.spinwheelId || !this.hubspotRecordId) {
      return {
        success: false,
        error: 'Missing required verification data'
      };
    }

    try {
      const response = await this.leadsService.verifyCode(
        code,
        formData.phone,
        this.spinwheelId,
        formData.email,
        this.hubspotRecordId
      );

      return {
        success: this.isSuccessStatus(response.status.code),
        error: this.isSuccessStatus(response.status.code) ? undefined : 'Failed to verify code'
      };
    } catch (err) {
      return this.handleApiError(err);
    }
  }

  async createContact(formData: FormData): Promise<ContactResult> {
    if (!this.spinwheelId || !this.hubspotRecordId) {
      return {
        success: false,
        status: 'ERROR',
        error: 'Missing required contact data'
      };
    }

    try {
      const response = await this.leadsService.createContact(
        this.spinwheelId,
        formData.email,
        this.hubspotRecordId
      );

      // Track verification attempts
      this.verificationAttempts++;

      // Store the first name and debt amount from the response
      if (response.firstName) {
        this.contactFirstName = response.firstName;
        storageController.setContactFirstName(response.firstName);
      }
      if (response.debtAmount) {
        this.debtAmount = response.debtAmount;
        storageController.setDebtAmount(response.debtAmount);
      }
      this.saveToStorage();

      // Handle qualified status
      if (response.state === 'accepted') {
        // Check for tier 2 qualification
        if (response.debtType === 'debt2') {
          return {
            success: true,
            status: 'accepted_tier2'
          };
        }
        return {
          success: true,
          status: 'accepted'
        };
      }

      // Handle unqualified status
      if (response.state === 'rejected') {
        return {
          success: true,
          status: 'rejected'
        };
      }

      // Handle error case
      return {
        success: false,
        status: 'ERROR',
        error: response.status.message || 'Failed to create contact'
      };
    } catch (err) {
      if (err instanceof ApiError && err.status.code === 422) {
        return {
          success: true,
          status: 'rejected'
        };
      }
      
      // Track failed attempt
      this.verificationAttempts++;
      this.saveToStorage();

      const error = this.handleApiError(err);
      return {
        success: false,
        status: 'ERROR',
        error: error.error,
        code: error.code
      };
    }
  }

  getVerificationAttempts(): number {
    return this.verificationAttempts;
  }

  private async fallbackToExperian(data: Partial<FormData>): Promise<{ success: boolean; qualified: boolean }> {
    const experianResponse = await this.leadsService.createContactExperian(
      data.email!,
      data.firstName!,
      data.lastName!,
      data.address1!,
      data.city!,
      data.state!,
      data.zipCode!,
      data.birthDate || '',
      data.phone || ''
    );
  
    if (experianResponse.firstName) {
      this.contactFirstName = experianResponse.firstName;
      storageController.setContactFirstName(experianResponse.firstName);
    }
    if (experianResponse.debtAmount) {
      this.debtAmount = experianResponse.debtAmount;
      storageController.setDebtAmount(experianResponse.debtAmount);
    }
    this.saveToStorage();

 // Handle qualified status
    if (experianResponse.state === 'accepted') {
      return {
        success: true,
        qualified: true
      };
    }

    // Handle unqualified status
    if (experianResponse.state === 'rejected') {
      return {
        success: true,
        qualified: false
      };
    }
    return {
      success: false,
      qualified: false,
    };
  }


  async submitApplication(formData: FormData, ipAddress: string): Promise<{ success: boolean; error?: string; offerId?: string, offerCount: number, storeUuid: string }> {
    try {
      const response = await this.leadsService.submitApplication(
        formData.email,
        formData.ssnLast4 || '',
        formData.loanAmount || 0,
        formData.loanPurpose || 'credit_card_refi',
        formData.employmentStatus || '',
        formData.employmentFrequency || '',
        formData.educationLevel || '',
        formData.annualIncome || 0,
        formData.propertyStatus || '',
        ipAddress,
        this.hubspotRecordId || undefined
      );

      return {
        success: this.isSuccessStatus(response.status.code),
        offerId: response.offerId,
        offerCount: response.offerCount,
        storeUuid: response.storeUuid,
        error: this.isSuccessStatus(response.status.code) ? undefined : 'Failed to submit application'
      };
    } catch (err) {
      const error = this.handleApiError(err);
      return {
        success: false,
        error: error.error,
        offerCount: 0,
        storeUuid: ''
      };
    }
  }

  async checkContactCenterAvailability(): Promise<AvailabilityResult> {
    try {
      const response = await this.leadsService.getContactCenterAvailability();
      const capacity = response.capacity || 0;
      
      return {
        success: this.isSuccessStatus(response.status.code),
        capacity,
        availability: this.getAvailabilityLevel(capacity)
      };
    } catch (err) {
      const error = this.handleApiError(err);
      return {
        ...error,
        availability: 'none'
      };
    }
  }

  async scheduleAppointment(meetingTime: Date): Promise<ScheduleResult> {
    if (!this.hubspotRecordId) {
      return {
        success: false,
        error: 'Missing required HubSpot record ID'
      };
    }

    try {
      const response = await this.leadsService.scheduleAppointment(
        this.hubspotRecordId,
        meetingTime.toISOString()
      );

      return {
        success: this.isSuccessStatus(response.status.code),
        error: this.isSuccessStatus(response.status.code) ? undefined : 'Failed to schedule appointment'
      };
    } catch (err) {
      return this.handleApiError(err);
    }
  }

  async connectCall(): Promise<CallResult> {
    if (!this.hubspotRecordId) {
      return {
        success: false,
        error: 'Missing required HubSpot record ID'
      };
    }

    try {
      const response = await this.leadsService.connectCall(this.hubspotRecordId);
      
      return {
        success: this.isSuccessStatus(response.status.code),
        from: response.from,
        error: this.isSuccessStatus(response.status.code) ? undefined : 'Failed to connect call'
      };
    } catch (err) {
      return this.handleApiError(err);
    }
  }

  // Track which success page variant was shown to customer
  async successPage(variantId: string): Promise<{ success: boolean; error?: string }> {
    if (!this.hubspotRecordId) {
      return {
        success: true
      };
    }
    try {
      const response = await this.leadsService.successPage(this.hubspotRecordId, variantId);
      return { success: this.isSuccessStatus(response.status.code) };
    } catch (err) {
      return { success: true };
    }
  }

  /**
   * Get availability for a specific date with caching
   * @param date - Date string in YYYY-MM-DD format or Date object
   * @returns Promise<DateAvailabilityResult> - Availability data with caching
   */
  async getDateAvailability(date: string | Date): Promise<DateAvailabilityResult> {
    // Normalize date to string format
    const dateString = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    
    // Check cache first
    if (this.isCacheValid(dateString)) {
      return this.availabilityCache[dateString].data;
    }

    try {
      const response = await this.leadsService.getAvailability(dateString);
      
      let result: DateAvailabilityResult;
      
      if (!this.isSuccessStatus(response.status.code)) {
        result = {
          success: false,
          date: dateString,
          timeslots: [],
          error: response.status.message,
          cachedAt: Date.now()
        };
      } else {
        // Transform the API response into our format
        const timeslots: TimeslotData[] = Object.entries(response.data).map(([timeSlot, data]) => ({
          timeSlot,
          isAvailable: data.isAvailable,
          capacity: data.capacity,
          booked: data.booked,
          slotsAvailable: data.slotsAvailable
        }));

        result = {
          success: true,
          date: dateString,
          totalCalls: response.totalCalls,
          timeslots,
          cachedAt: Date.now()
        };
      }

      // Cache the result
      this.availabilityCache[dateString] = {
        data: result,
        cachedAt: Date.now()
      };
      
      this.saveAvailabilityCache();
      
      return result;
    } catch (err) {
      const error = this.handleApiError(err);
      
      const result: DateAvailabilityResult = {
        success: false,
        date: dateString,
        timeslots: [],
        error: error.error,
        cachedAt: Date.now()
      };
      
      // Cache error results for a shorter time to avoid repeated failures
      this.availabilityCache[dateString] = {
        data: result,
        cachedAt: Date.now()
      };
      
      this.saveAvailabilityCache();
      
      return result;
    }
  }

  /**
   * Check if a specific timeslot is available on a given date
   * @param date - Date string in YYYY-MM-DD format or Date object
   * @param timeSlot - Time slot in HH:MM format (e.g., "09:00")
   * @returns Promise<boolean> - True if available
   */
  async isTimeslotAvailable(date: string | Date, timeSlot: string): Promise<boolean> {
    const availability = await this.getDateAvailability(date);
    
    if (!availability.success) {
      return false;
    }
    
    const slot = availability.timeslots.find(slot => slot.timeSlot === timeSlot);
    return slot ? slot.isAvailable : false;
  }

  /**
   * Get all available timeslots for a specific date
   * @param date - Date string in YYYY-MM-DD format or Date object
   * @returns Promise<string[]> - Array of available timeslots in HH:MM format
   */
  async getAvailableTimeslots(date: string | Date): Promise<string[]> {
    const availability = await this.getDateAvailability(date);
    
    if (!availability.success) {
      return [];
    }
    
    return availability.timeslots
      .filter(slot => slot.isAvailable)
      .map(slot => slot.timeSlot);
  }

  /**
   * Clear availability cache (useful for testing or manual refresh)
   */
  clearAvailabilityCache(): void {
    this.availabilityCache = {};
    sessionStorage.removeItem(STORAGE_KEYS.AVAILABILITY_CACHE);
  }

  // Reset all session data
  public reset(): void {
    this.spinwheelId = '';
    this.hubspotRecordId = '';
    this.verificationAttempts = 0;
    this.contactFirstName = '';
    this.debtAmount = 0;
    this.clearStorage();
  }
}

// Export singleton instance for global access
export const formController = new FormController();